/**
 * WebSocket Routes module for frontend (INBOUND - Receiving messages)
 * Handles real-time message routing via WebSocket
 *
 * This module ONLY handles receiving and routing messages.
 * Connection management is handled in outbound.ts
 *
 * Message flow:
 * 1. WebSocket receives message from backend (onmessage handler)
 * 2. Message is validated
 * 3. Message is routed to the appropriate channel handler
 * 4. Handler processes and updates the relevant component
 */

import {
    Connection,
    VishMakerPayload,
    validateVishmakerPayload,
    Channel
} from './outbound';

// -----------------------------
// Types
// -----------------------------

/**
 * Channel handler function type
 * Processes incoming messages for a specific channel
 */
export type ChannelHandler = (payload: VishMakerPayload) => void;

/**
 * WebSocket connection state
 */
export type ConnectionState = 'disconnected' | 'connected';

// -----------------------------
// Utility functions
// -----------------------------

function _logger() {
    return {
        info: (msg: string) => console.log(msg),
        error: (msg: string) => console.error(msg),
        warn: (msg: string) => console.warn(msg),
        debug: (msg: string) => console.debug(msg)
    };
}

// -----------------------------
// Message Router (INBOUND - Receiving only)
// -----------------------------

/**
 * MessageRouter - handles receiving and routing incoming WebSocket messages
 * This class ONLY handles message routing, not connection management
 * 
 * Tracks two timestamps for connection health:
 * - lastPongTimestamp: When a pong message is received (health channel)
 * - lastResponseReceivedTimestamp: When any payload is received (any channel)
 * 
 * Connection is considered healthy if either timestamp is within 2 minutes
 */
export class MessageRouter {
    // Channel handlers - components register their handlers here
    private channelHandlers: Map<Channel, ChannelHandler> = new Map();

    // Timestamp tracking for connection health
    private lastPongTimestamp: number | null = null;
    private lastResponseReceivedTimestamp: number | null = null;
    
    // Connection health timeout (2 minutes in milliseconds)
    private readonly HEALTH_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

    /**
     * Register a handler for a specific channel
     * Components call this to receive messages for their channel
     *
     * @example
     * // In a chat component:
     * router.setChannelHandler('chat', (payload) => {
     *   setChatMessages(prev => [...prev, payload]);
     * });
     */
    setChannelHandler(channel: Channel, handler: ChannelHandler): void {
        this.channelHandlers.set(channel, handler);
    }

    /**
     * Remove a channel handler
     */
    removeChannelHandler(channel: Channel): void {
        this.channelHandlers.delete(channel);
    }

    /**
     * Handle incoming WebSocket message
     * Routes message to the appropriate channel handler
     * 
     * This is called by outbound.ts when a message is received
     */
    handleIncomingMessage(rawData: string, conn: Connection | null): void {
        if (!conn) return;

        conn.touch();

        // Parse JSON
        let msgData: Record<string, any>;
        try {
            msgData = JSON.parse(rawData);
        } catch (error) {
            _logger().error('Invalid JSON received');
            return;
        }

        // Log health channel messages in detail
        if (msgData.channel === 'health') {
            //console.log(`📥 [Health Check] Raw WebSocket message received`);
            //console.log(`   └─ Raw data (${rawData.length} bytes):`, rawData);
            //console.log(`   └─ Parsed message:`, msgData);
        }

        // Validate the payload
        const [isValid, errorMsg, validatedData] = validateVishmakerPayload(msgData);
        if (!isValid || !validatedData) {
            _logger().error(`Invalid VishMakerPayload: ${errorMsg}`);
            return;
        }

        const payload = validatedData;
        const channel = payload.channel;

        if (!channel) {
            _logger().error('Missing channel in payload');
            return;
        }

        // Log payload for logs channel with response type (only log_batch type, not the full payload)
        if (channel === 'logs' && payload.type === 'response' && payload.body?.kind === 'logs' && payload.body?.payload?.type === 'log_batch') {
            const lines = payload.body.payload.lines || [];
            //console.log(`📥 [Logs Response] Received ${lines.length} log lines`);
        }

        // Update lastResponseReceivedTimestamp for any received message
        const now = Date.now();
        this.lastResponseReceivedTimestamp = now;

        // Update lastPongTimestamp if this is a pong message
        if (channel === 'health') {
            const payloadData = payload.body?.payload || payload.body || {};
            const message = payloadData?.message;
            if (message === 'pong') {
                this.lastPongTimestamp = now;
            }
        }

        // Route to registered channel handler
        const handler = this.channelHandlers.get(channel);
        if (handler) {
            try {
                handler(payload);
            } catch (error) {
                _logger().error(`Error in channel handler ${channel}: ${error}`);
            }
        }
    }

    /**
     * Get a channel handler (for health check to wrap)
     */
    getChannelHandler(channel: Channel): ChannelHandler | undefined {
        return this.channelHandlers.get(channel);
    }

    /**
     * Get timestamp information for connection health checking
     */
    getTimestamps(): {
        lastPongTimestamp: number | null;
        lastResponseReceivedTimestamp: number | null;
        timeSinceLastPong: number | null;
        timeSinceLastResponse: number | null;
    } {
        const now = Date.now();
        return {
            lastPongTimestamp: this.lastPongTimestamp,
            lastResponseReceivedTimestamp: this.lastResponseReceivedTimestamp,
            timeSinceLastPong: this.lastPongTimestamp ? now - this.lastPongTimestamp : null,
            timeSinceLastResponse: this.lastResponseReceivedTimestamp ? now - this.lastResponseReceivedTimestamp : null,
        };
    }

    /**
     * Check if connection is healthy based on timestamps
     * Connection is healthy if either lastPongTimestamp or lastResponseReceivedTimestamp
     * is within the last 2 minutes
     * 
     * @returns true if connection is healthy, false otherwise
     */
    isConnectionHealthy(): boolean {
        const now = Date.now();
        
        // Check if we have a recent pong (within 2 minutes)
        const hasRecentPong = this.lastPongTimestamp !== null && 
                             (now - this.lastPongTimestamp) < this.HEALTH_TIMEOUT_MS;
        
        // Check if we have a recent response (within 2 minutes)
        const hasRecentResponse = this.lastResponseReceivedTimestamp !== null && 
                                 (now - this.lastResponseReceivedTimestamp) < this.HEALTH_TIMEOUT_MS;
        
        return hasRecentPong || hasRecentResponse;
    }

    /**
     * Reset timestamps (called on disconnect)
     */
    resetTimestamps(): void {
        this.lastPongTimestamp = null;
        this.lastResponseReceivedTimestamp = null;
    }
}

// Export singleton message router instance
let _messageRouter: MessageRouter | null = null;

/**
 * Get the singleton message router instance
 */
export function getMessageRouter(): MessageRouter {
    if (!_messageRouter) {
        _messageRouter = new MessageRouter();
    }
    return _messageRouter;
}
